












































































































































































































import { compact, fromPairs, isEqual } from 'lodash-es'
import { ButtonGroup, Icon, Button, Checkbox, Card, Select } from '@alpha/ink'
import preQualificationTypes from '@/utilities/pq-types'
import { Vue, Component, Prop } from 'vue-property-decorator'
import AnimatedContainer from '@/components/AnimatedContainer.vue'
import AudienceManagerModalFilterCard from './AudienceManagerModalFilterCard.vue'
import Audience, { audienceNameMaxLength, AudienceCriteriaType, AudienceCriteria, GeographicLocation, GeographicLocationType, NewAudienceValidationError, NewAudienceValidationErrorType } from './Audience'
import AgeRangeSelector from './AgeRangeSelector.vue'

@Component({
  components: {
    ButtonGroup,
    Icon,
    Button,
    Checkbox,
    Card,
    Select,
    AnimatedContainer,
    AudienceManagerModalFilterCard,
    AgeRangeSelector
  }
})

export default class AudienceBuilderContent extends Vue {
  @Prop({ required: true }) readonly audience!: Audience
  @Prop({ required: true }) readonly defaultCriteria!: AudienceCriteria
  @Prop({ required: true }) readonly activeCriteriaTypes!: AudienceCriteriaType[]
  @Prop({ default: false }) readonly hasInternationalFeature!: boolean
  @Prop({ default: () => ({}) }) readonly internationalConfigs!: { MaxCountries: number, CountriesSelected: string[] }
  @Prop({ required: true }) readonly validationErrors!: NewAudienceValidationError[]

  AudienceCriteriaType = AudienceCriteriaType
  GeographicLocationType = GeographicLocationType
  legacyInternationalCountries = [
    'Canada',
    'United Kingdom',
    'India'
  ]

  maxTitleLength = audienceNameMaxLength

  get validationErrorsByCriteriaType (): Partial<Record<AudienceCriteriaType, NewAudienceValidationError>> {
    return fromPairs(compact(this.validationErrors.map(e => {
      switch (e.type) {
        case NewAudienceValidationErrorType.NAME_UNDEFINED:
        case NewAudienceValidationErrorType.NAME_EXISTS:
          return
      }

      return [e.criteriaType, e]
    })))
  }

  get invalidNameErrorMessage (): string | undefined {
    if (this.validationErrors.find(e => e.type === NewAudienceValidationErrorType.NAME_UNDEFINED)) {
      return 'Audience name is required'
    }

    if (this.validationErrors.find(e => e.type === NewAudienceValidationErrorType.NAME_EXISTS)) {
      return 'Audience name already exists'
    }
  }

  get geographicLocation () {
    return this.audience.criteria[AudienceCriteriaType.GEOGRAPHIC_LOCATION]
  }

  get geographicLocationType () {
    return this.geographicLocation ? this.geographicLocation.type : undefined
  }

  getDefaultGeographicLocation (geographicLocationType: GeographicLocationType): GeographicLocation {
    switch (geographicLocationType) {
      case GeographicLocationType.INTERNATIONAL: return {
        type: GeographicLocationType.INTERNATIONAL,
        countries: []
      }
      case GeographicLocationType.USA: return {
        type: GeographicLocationType.USA
      }
      case GeographicLocationType.USA_STATES: return {
        type: GeographicLocationType.USA_STATES,
        states: []
      }
    }
  }

  get geographicLocationStateItems () {
    if (this.geographicLocation && this.geographicLocation.type === GeographicLocationType.USA_STATES) {
      return this.geographicLocation.states.map(state => this.stateOptions.find(o => o.label === state))
    }
  }

  get activeCriteriaTypesNotCustomOrDefault () {
    return this.activeCriteriaTypes.filter((criteria: AudienceCriteriaType) => ![AudienceCriteriaType.ADDITIONAL, AudienceCriteriaType.AGE, AudienceCriteriaType.GEOGRAPHIC_LOCATION].includes(criteria))
  }

  get titleCharLimit () {
    return `${this.audience.name.length}/${this.maxTitleLength}`
  }

  setGeographicLocationStateItems (stateOptions: { label: string, value: string }[]) {
    this.updateCriteria({
      [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: {
        type: GeographicLocationType.USA_STATES,
        states: stateOptions.map(o => o.label)
      }
    })
  }

  toggleCountry (country: string) {
    const geographicLocation = this.geographicLocation
    if (!geographicLocation) return
    if (geographicLocation.type !== GeographicLocationType.INTERNATIONAL) return
    let countries: string[] = []
    if (geographicLocation.countries.includes(country)) {
      countries = geographicLocation.countries.filter(c => c !== country)
    } else if (!this.hasInternationalFeature) {
      countries = [country]
    } else {
      countries = [...geographicLocation.countries, country]
    }

    this.updateCriteria({
      [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: {
        type: GeographicLocationType.INTERNATIONAL,
        countries
      }
    })
  }

  setGeographicLocationType (geographicLocationType: GeographicLocationType) {
    if (this.geographicLocationType === geographicLocationType) return
    this.updateCriteria({
      [AudienceCriteriaType.GEOGRAPHIC_LOCATION]: this.getDefaultGeographicLocation(geographicLocationType)
    })
  }

  isCriteriaTypeActive (criteriaType: AudienceCriteriaType) {
    return this.activeCriteriaTypes.includes(criteriaType)
  }

  hasCriteriaChanged (criteriaType: AudienceCriteriaType) {
    return !isEqual(this.audience.criteria[criteriaType], this.defaultCriteria[criteriaType])
  }

  revertCriteria (audienceCriteriaType: AudienceCriteriaType) {
    this.updateCriteria({ [audienceCriteriaType]: this.defaultCriteria[audienceCriteriaType] })
  }

  updateCriteria (audienceCriteriaUpdate: Partial<AudienceCriteria>) {
    this.$emit('updateCriteria', audienceCriteriaUpdate)
  }

  get stateOptions () {
    return preQualificationTypes.State.options.filter(opt => opt.title !== 'Not Applicable').map(opt => (
      {
        label: opt.title,
        value: opt.abbrev
      }
    ))
  }

  setCustomCriteria (value: string) {
    this.updateCriteria({ [AudienceCriteriaType.ADDITIONAL]: value })
  }

  deactivateCriteriaType (criteriaType: AudienceCriteriaType) {
    this.$emit('setCriteriaTypeActive', criteriaType, false)
  }

  updatePrequal (payload: { prequal: AudienceCriteriaType, values: string[] | number[] }) {
    this.updateCriteria({ [payload.prequal]: payload.values })
  }
}
